export interface QuizQuestion {
    id: string;
    type: 'abc' | 'yesno' | 'player_image' | 'club_crest';
    question: string;
    options: string[];
    answer: string;
    theme?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    avatarEmoji?: string; // used for player_image representation
    icon?: string;
    crests?: { name: string; color: string; abbreviation: string }[]; // for club_crest type
}

// Localized high-quality fallback questions
export const LOCALIZED_QUESTIONS: Record<string, QuizQuestion[]> = {
    en: [
        {
            id: "q1",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Which nation won the first-ever FIFA World Cup in 1930?",
            options: ["Argentina", "Uruguay", "Brazil", "Italy"],
            answer: "Uruguay"
        },
        {
            id: "q2",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Who is the all-time top scorer in FIFA World Cup history with 16 goals?",
            options: ["Miroslav Klose", "Ronaldo Nazário", "Gerd Müller", "Pelé"],
            answer: "Miroslav Klose"
        },
        {
            id: "q3",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Has a country from Africa ever reached the FIFA World Cup Final?",
            options: ["Yes", "No"],
            answer: "No"
        },
        {
            id: "q4",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Did modern football icon Cristiano Ronaldo start his professional career at Sporting CP?",
            options: ["Yes", "No"],
            answer: "Yes"
        },
        {
            id: "q5",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Guess the legend from their iconic jersey number, nation, and current club:",
            options: ["Lionel Messi", "Cristiano Ronaldo", "Diego Maradona", "Zinedine Zidane"],
            answer: "Lionel Messi",
            avatarEmoji: "🐐 🔟 🇦🇷 Inter Miami | Born in Rosario"
        },
        {
            id: "q6",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Guess the French striker from his achievements and current powerhouse club:",
            options: ["Kylian Mbappé", "Karim Benzema", "Olivier Giroud", "Antoine Griezmann"],
            answer: "Kylian Mbappé",
            avatarEmoji: "🐢 ⚡ 🇫🇷 Real Madrid | World Cup Golden Boot"
        },
        {
            id: "q7",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Guess the Brazilian forward who won two World Cups and is nicknamed El Fenómeno:",
            options: ["Ronaldinho", "Ronaldo Nazário", "Neymar Jr", "Kaká"],
            answer: "Ronaldo Nazário",
            avatarEmoji: "🇧🇷 9️⃣ ⚽ R9 | World Cup Winner 1994 & 2002"
        },
        {
            id: "q8",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identify the player who represented all of these clubs during their senior career:",
            options: ["Lionel Messi", "Neymar Jr", "Luis Suárez", "Ronaldinho"],
            answer: "Neymar Jr",
            crests: [
                { name: "Santos FC", color: "bg-zinc-800 text-zinc-100 border-zinc-700", abbreviation: "SAN" },
                { name: "FC Barcelona", color: "bg-red-700 text-blue-300 border-blue-900", abbreviation: "BAR" },
                { name: "Paris Saint-Germain", color: "bg-blue-950 text-red-500 border-red-900", abbreviation: "PSG" },
                { name: "Al Hilal SFC", color: "bg-blue-600 text-white border-blue-400", abbreviation: "HIL" }
            ]
        },
        {
            id: "q9",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "hard",
            question: "Which legendary striker has played for all of these prominent European giants?",
            options: ["Zlatan Ibrahimović", "Cristiano Ronaldo", "Robert Lewandowski", "Karim Benzema"],
            answer: "Zlatan Ibrahimović",
            crests: [
                { name: "Ajax", color: "bg-red-600 text-white border-rose-300", abbreviation: "AJX" },
                { name: "Juventus", color: "bg-neutral-900 text-white border-zinc-700", abbreviation: "JUV" },
                { name: "Inter Milan", color: "bg-slate-900 text-blue-500 border-blue-700", abbreviation: "INT" },
                { name: "Barcelona", color: "bg-emerald-950 text-rose-500 border-rose-800", abbreviation: "BAR" }
            ]
        },
        {
            id: "q10",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Which player holds the record for the most goals in a single calendar year (91 goals in 2012)?",
            options: ["Cristiano Ronaldo", "Robert Lewandowski", "Lionel Messi", "Gerd Müller"],
            answer: "Lionel Messi"
        },
        {
            id: "q11",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Did England win their only FIFA World Cup title on home soil in the historic year of 1966?",
            options: ["Yes", "No"],
            answer: "Yes"
        },
        {
            id: "q12",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identify the elite playmaker midfielder who represented these world-class clubs:",
            options: ["Luka Modrić", "Toni Kroos", "Kevin De Bruyne", "Casemiro"],
            answer: "Toni Kroos",
            crests: [
                { name: "Bayern Munich", color: "bg-red-600 text-white border-red-400", abbreviation: "FCB" },
                { name: "Bayer Leverkusen", color: "bg-red-750 text-black border-zinc-800", abbreviation: "B04" },
                { name: "Real Madrid", color: "bg-white text-zinc-900 border-yellow-500", abbreviation: "RMA" }
            ]
        },
        {
            id: "q13",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Which country has won the most FIFA World Cup titles in football history (5 titles)?",
            options: ["Germany", "Italy", "Argentina", "Brazil"],
            answer: "Brazil"
        },
        {
            id: "q14",
            type: 'yesno',
            theme: "Sport",
            difficulty: "hard",
            question: "Is Uruguayan forward Luis Suárez the current all-time top scorer in Copa América history?",
            options: ["Yes", "No"],
            answer: "No"
        },
        {
            id: "q15",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Guess the dominant Premier League midfielder from his club and country icons:",
            options: ["Kevin De Bruyne", "Luka Modrić", "Andrés Iniesta", "Eden Hazard"],
            answer: "Kevin De Bruyne",
            avatarEmoji: "👑 🪄 🇧🇪 Manchester City | Ultimate Assist King 🅰️"
        },
        {
            id: "q16",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identify the prolific striker who played for these three development and giant clubs:",
            options: ["Erling Haaland", "Jude Bellingham", "Robert Lewandowski", "Harry Kane"],
            answer: "Erling Haaland",
            crests: [
                { name: "Salzburg", color: "bg-red-600 text-white border-zinc-200", abbreviation: "RBS" },
                { name: "Dortmund", color: "bg-yellow-400 text-black border-yellow-600", abbreviation: "BVB" },
                { name: "Manchester City", color: "bg-sky-400 text-slate-800 border-sky-200", abbreviation: "MCI" }
            ]
        },
        {
            id: "q17",
            type: 'abc',
            theme: "Sport",
            difficulty: "hard",
            question: "Which player won the Golden Ball (Best Player) award of the 2018 FIFA World Cup in Russia?",
            options: ["Antoine Griezmann", "Luka Modrić", "Eden Hazard", "Kylian Mbappé"],
            answer: "Luka Modrić"
        },
        {
            id: "q18",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Did Pele win three FIFA World Cups with Brazil (1958, 1962, and 1970)?",
            options: ["Yes", "No"],
            answer: "Yes"
        },
        {
            id: "q19",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identify the legendary French forward who played for these legendary teams:",
            options: ["Karim Benzema", "Thierry Henry", "Zinedine Zidane", "Nicolas Anelka"],
            answer: "Thierry Henry",
            crests: [
                { name: "Monaco", color: "bg-red-600 text-white border-zinc-200", abbreviation: "ASM" },
                { name: "Juventus", color: "bg-neutral-900 text-zinc-105 border-zinc-700", abbreviation: "JUV" },
                { name: "Arsenal", color: "bg-red-550 text-white border-zinc-100", abbreviation: "ARS" },
                { name: "FC Barcelona", color: "bg-rose-900 text-blue-200 border-blue-900", abbreviation: "BAR" }
            ]
        },
        {
            id: "q20",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Guess this legendary Italian goalkeeper who won the 2006 World Cup and reached over 1000 career games:",
            options: ["Gianluigi Buffon", "Iker Casillas", "Manuel Neuer", "Dino Zoff"],
            answer: "Gianluigi Buffon",
            avatarEmoji: "🇮🇹 🧤 👑 Juventus Legend | 2006 World Champion"
        }
    ],
    es: [
        {
            id: "q1",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Qué país ganó la primera Copa Mundial de la FIFA en 1930?",
            options: ["Argentina", "Uruguay", "Brasil", "Italia"],
            answer: "Uruguay"
        },
        {
            id: "q2",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "¿Quién es el máximo goleador histórico de la Copa Mundial de la FIFA con 16 goles?",
            options: ["Miroslav Klose", "Ronaldo Nazário", "Gerd Müller", "Pelé"],
            answer: "Miroslav Klose"
        },
        {
            id: "q3",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Ha llegado alguna vez un país de África a la final de la Copa Mundial de la FIFA?",
            options: ["Sí", "No"],
            answer: "No"
        },
        {
            id: "q4",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Comenzó Cristiano Ronaldo su carrera profesional en el Sporting CP?",
            options: ["Sí", "No"],
            answer: "Sí"
        },
        {
            id: "q5",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Adivina la leyenda por su icónico número de camiseta, país y club actual:",
            options: ["Lionel Messi", "Cristiano Ronaldo", "Diego Maradona", "Zinedine Zidane"],
            answer: "Lionel Messi",
            avatarEmoji: "🐐 🔟 🇦🇷 Inter Miami | Nacido en Rosario"
        },
        {
            id: "q6",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Adivina al delantero francés por sus logros y su club potente actual:",
            options: ["Kylian Mbappé", "Karim Benzema", "Olivier Giroud", "Antoine Griezmann"],
            answer: "Kylian Mbappé",
            avatarEmoji: "🐢 ⚡ 🇫🇷 Real Madrid | Bota de Oro del Mundial"
        },
        {
            id: "q7",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivina al delantero brasileño que ganó dos Mundiales y es apodado El Fenómeno:",
            options: ["Ronaldinho", "Ronaldo Nazário", "Neymar Jr", "Kaká"],
            answer: "Ronaldo Nazário",
            avatarEmoji: "🇧🇷 9️⃣ ⚽ R9 | Campeón del Mundo 1994 y 2002"
        },
        {
            id: "q8",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifica al jugador que representó a todos estos clubes durante su carrera profesional:",
            options: ["Lionel Messi", "Neymar Jr", "Luis Suárez", "Ronaldinho"],
            answer: "Neymar Jr",
            crests: [
                { name: "Santos FC", color: "bg-zinc-800 text-zinc-100 border-zinc-700", abbreviation: "SAN" },
                { name: "FC Barcelona", color: "bg-red-700 text-blue-300 border-blue-900", abbreviation: "BAR" },
                { name: "Paris Saint-Germain", color: "bg-blue-950 text-red-500 border-red-900", abbreviation: "PSG" },
                { name: "Al Hilal SFC", color: "bg-blue-600 text-white border-blue-400", abbreviation: "HIL" }
            ]
        },
        {
            id: "q9",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "hard",
            question: "¿Qué legendario delantero ha jugado en todos estos gigantes europeos?",
            options: ["Zlatan Ibrahimović", "Cristiano Ronaldo", "Robert Lewandowski", "Karim Benzema"],
            answer: "Zlatan Ibrahimović",
            crests: [
                { name: "Ajax", color: "bg-red-600 text-white border-rose-300", abbreviation: "AJX" },
                { name: "Juventus", color: "bg-neutral-900 text-white border-zinc-700", abbreviation: "JUV" },
                { name: "Inter Milan", color: "bg-slate-900 text-blue-500 border-blue-700", abbreviation: "INT" },
                { name: "Barcelona", color: "bg-emerald-950 text-rose-500 border-rose-800", abbreviation: "BAR" }
            ]
        },
        {
            id: "q10",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "¿Qué jugador ostenta el récord de más goles en un solo año natural (91 goles en 2012)?",
            options: ["Cristiano Ronaldo", "Robert Lewandowski", "Lionel Messi", "Gerd Müller"],
            answer: "Lionel Messi"
        },
        {
            id: "q11",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Ganó Inglaterra su único título de Copa del Mundo en su propio país en el histórico año de 1966?",
            options: ["Sí", "No"],
            answer: "Sí"
        },
        {
            id: "q12",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifica al creador de juego de élite que representó a estos clubes de clase mundial:",
            options: ["Luka Modrić", "Toni Kroos", "Kevin De Bruyne", "Casemiro"],
            answer: "Toni Kroos",
            crests: [
                { name: "Bayern Munich", color: "bg-red-600 text-white border-red-400", abbreviation: "FCB" },
                { name: "Bayer Leverkusen", color: "bg-red-750 text-black border-zinc-800", abbreviation: "B04" },
                { name: "Real Madrid", color: "bg-white text-zinc-900 border-yellow-500", abbreviation: "RMA" }
            ]
        },
        {
            id: "q13",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Qué país ha ganado más campeonatos de la Copa Mundial de la FIFA en la historia del fútbol (5 títulos)?",
            options: ["Alemania", "Italia", "Argentina", "Brasil"],
            answer: "Brasil"
        },
        {
            id: "q14",
            type: 'yesno',
            theme: "Sport",
            difficulty: "hard",
            question: "¿Es el delantero uruguayo Luis Suárez el máximo goleador histórico de la Copa América?",
            options: ["Sí", "No"],
            answer: "No"
        },
        {
            id: "q15",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivina al centrocampista dominante de la Premier League por sus iconos de club y país:",
            options: ["Kevin De Bruyne", "Luka Modrić", "Andrés Iniesta", "Eden Hazard"],
            answer: "Kevin De Bruyne",
            avatarEmoji: "👑 🪄 🇧🇪 Manchester City | Rey Supremo de las Asistencias 🅰️"
        },
        {
            id: "q16",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifica al goleador prolífico que jugó para estos tres clubes de desarrollo y gigantes:",
            options: ["Erling Haaland", "Jude Bellingham", "Robert Lewandowski", "Harry Kane"],
            answer: "Erling Haaland",
            crests: [
                { name: "Salzburg", color: "bg-red-600 text-white border-zinc-200", abbreviation: "RBS" },
                { name: "Dortmund", color: "bg-yellow-400 text-black border-yellow-600", abbreviation: "BVB" },
                { name: "Manchester City", color: "bg-sky-400 text-slate-800 border-sky-200", abbreviation: "MCI" }
            ]
        },
        {
            id: "q17",
            type: 'abc',
            theme: "Sport",
            difficulty: "hard",
            question: "¿Qué jugador ganó el Balón de Oro (Mejor Jugador) en la Copa Mundial de la FIFA 2018 en Rusia?",
            options: ["Antoine Griezmann", "Luka Modrić", "Eden Hazard", "Kylian Mbappé"],
            answer: "Luka Modrić"
        },
        {
            id: "q18",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "¿Ganó Pelé tres Copas Mundiales de la FIFA con Brasil (1958, 1962 y 1970)?",
            options: ["Sí", "No"],
            answer: "Sí"
        },
        {
            id: "q19",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifica al legendario delantero francés que jugó en estos míticos equipos:",
            options: ["Karim Benzema", "Thierry Henry", "Zinedine Zidane", "Nicolas Anelka"],
            answer: "Thierry Henry",
            crests: [
                { name: "Monaco", color: "bg-red-600 text-white border-zinc-200", abbreviation: "ASM" },
                { name: "Juventus", color: "bg-neutral-900 text-zinc-105 border-zinc-700", abbreviation: "JUV" },
                { name: "Arsenal", color: "bg-red-550 text-white border-zinc-100", abbreviation: "ARS" },
                { name: "FC Barcelona", color: "bg-rose-900 text-blue-200 border-blue-900", abbreviation: "BAR" }
            ]
        },
        {
            id: "q20",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivina a este legendario portero italiano que ganó el Mundial de 2006 y superó los 1000 partidos profesionales:",
            options: ["Gianluigi Buffon", "Iker Casillas", "Manuel Neuer", "Dino Zoff"],
            answer: "Gianluigi Buffon",
            avatarEmoji: "🇮🇹 🧤 👑 Leyenda de la Juventus | Campeón del Mundo 2006"
        }
    ],
    pt: [
        {
            id: "q1",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Qual país venceu a primeira Copa do Mundo da FIFA em 1930?",
            options: ["Argentina", "Uruguai", "Brasil", "Itália"],
            answer: "Uruguai"
        },
        {
            id: "q2",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Quem é o maior artilheiro da história da Copa do Mundo da FIFA com 16 gols?",
            options: ["Miroslav Klose", "Ronaldo Nazário", "Gerd Müller", "Pelé"],
            answer: "Miroslav Klose"
        },
        {
            id: "q3",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Algum país da África já chegou à final da Copa do Mundo da FIFA?",
            options: ["Sim", "Não"],
            answer: "Não"
        },
        {
            id: "q4",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "O ícone do futebol moderno Cristiano Ronaldo começou sua carreira profissional no Sporting CP?",
            options: ["Sim", "Não"],
            answer: "Sim"
        },
        {
            id: "q5",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Adivinhe a lenda pelo seu número de camisa, seleção e clube atual:",
            options: ["Lionel Messi", "Cristiano Ronaldo", "Diego Maradona", "Zinedine Zidane"],
            answer: "Lionel Messi",
            avatarEmoji: "🐐 🔟 🇦🇷 Inter Miami | Nascido em Rosário"
        },
        {
            id: "q6",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Adivinhe o atacante francês pelas suas conquistas e clube gigante atual:",
            options: ["Kylian Mbappé", "Karim Benzema", "Olivier Giroud", "Antoine Griezmann"],
            answer: "Kylian Mbappé",
            avatarEmoji: "🐢 ⚡ 🇫🇷 Real Madrid | Chuteira de Ouro da Copa"
        },
        {
            id: "q7",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivinhe o atacante brasileiro que venceu duas Copas e tem o apelido de Fenômeno:",
            options: ["Ronaldinho", "Ronaldo Nazário", "Neymar Jr", "Kaká"],
            answer: "Ronaldo Nazário",
            avatarEmoji: "🇧🇷 9️⃣ ⚽ R9 | Campeão da Copa do Mundo 1994 e 2002"
        },
        {
            id: "q8",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifique o jogador que representou todos estes clubes durante sua carreira principal:",
            options: ["Lionel Messi", "Neymar Jr", "Luis Suárez", "Ronaldinho"],
            answer: "Neymar Jr",
            crests: [
                { name: "Santos FC", color: "bg-zinc-800 text-zinc-100 border-zinc-700", abbreviation: "SAN" },
                { name: "FC Barcelona", color: "bg-red-700 text-blue-300 border-blue-900", abbreviation: "BAR" },
                { name: "Paris Saint-Germain", color: "bg-blue-950 text-red-500 border-red-900", abbreviation: "PSG" },
                { name: "Al Hilal SFC", color: "bg-blue-600 text-white border-blue-400", abbreviation: "HIL" }
            ]
        },
        {
            id: "q9",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "hard",
            question: "Qual atacante lendário jogou por todos estes gigantes europeus?",
            options: ["Zlatan Ibrahimović", "Cristiano Ronaldo", "Robert Lewandowski", "Karim Benzema"],
            answer: "Zlatan Ibrahimović",
            crests: [
                { name: "Ajax", color: "bg-red-600 text-white border-rose-300", abbreviation: "AJX" },
                { name: "Juventus", color: "bg-neutral-900 text-white border-zinc-700", abbreviation: "JUV" },
                { name: "Inter Milan", color: "bg-slate-900 text-blue-500 border-blue-700", abbreviation: "INT" },
                { name: "Barcelona", color: "bg-emerald-950 text-rose-500 border-rose-800", abbreviation: "BAR" }
            ]
        },
        {
            id: "q10",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Qual jogador detém o recorde de mais gols em um único ano civil (91 gols em 2012)?",
            options: ["Cristiano Ronaldo", "Robert Lewandowski", "Lionel Messi", "Gerd Müller"],
            answer: "Lionel Messi"
        },
        {
            id: "q11",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "A Inglaterra venceu seu único título da Copa do Mundo em casa no histórico ano de 1966?",
            options: ["Sim", "Não"],
            answer: "Sim"
        },
        {
            id: "q12",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifique o meio-campista de elite que representou estes clubes de classe mundial:",
            options: ["Luka Modrić", "Toni Kroos", "Kevin De Bruyne", "Casemiro"],
            answer: "Toni Kroos",
            crests: [
                { name: "Bayern Munich", color: "bg-red-600 text-white border-red-400", abbreviation: "FCB" },
                { name: "Bayer Leverkusen", color: "bg-red-750 text-black border-zinc-800", abbreviation: "B04" },
                { name: "Real Madrid", color: "bg-white text-zinc-900 border-yellow-500", abbreviation: "RMA" }
            ]
        },
        {
            id: "q13",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Qual país venceu mais títulos da Copa do Mundo da FIFA na história (5 títulos)?",
            options: ["Alemanha", "Itália", "Argentina", "Brasil"],
            answer: "Brasil"
        },
        {
            id: "q14",
            type: 'yesno',
            theme: "Sport",
            difficulty: "hard",
            question: "O atacante uruguaio Luis Suárez é o atual maior artilheiro da história da Copa América?",
            options: ["Sim", "Não"],
            answer: "Não"
        },
        {
            id: "q15",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivinhe o meio-campista dominante da Premier League pelas dicas de clube e seleção:",
            options: ["Kevin De Bruyne", "Luka Modrić", "Andrés Iniesta", "Eden Hazard"],
            answer: "Kevin De Bruyne",
            avatarEmoji: "👑 🪄 🇧🇪 Manchester City | Rei Supremo de Assistências 🅰️"
        },
        {
            id: "q16",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifique o atacante prolífico que jogou por estes três clubes de desenvolvimento e gigantes:",
            options: ["Erling Haaland", "Jude Bellingham", "Robert Lewandowski", "Harry Kane"],
            answer: "Erling Haaland",
            crests: [
                { name: "Salzburg", color: "bg-red-600 text-white border-zinc-200", abbreviation: "RBS" },
                { name: "Dortmund", color: "bg-yellow-400 text-black border-yellow-600", abbreviation: "BVB" },
                { name: "Manchester City", color: "bg-sky-400 text-slate-800 border-sky-200", abbreviation: "MCI" }
            ]
        },
        {
            id: "q17",
            type: 'abc',
            theme: "Sport",
            difficulty: "hard",
            question: "Qual jogador ganhou a Bola de Ouro (Melhor Jogador) da Copa do Mundo de 2018 na Rússia?",
            options: ["Antoine Griezmann", "Luka Modrić", "Eden Hazard", "Kylian Mbappé"],
            answer: "Luka Modrić"
        },
        {
            id: "q18",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Pelé venceu três Copas do Mundo da FIFA com o Brasil (1958, 1962 e 1970)?",
            options: ["Sim", "Não"],
            answer: "Sim"
        },
        {
            id: "q19",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifique o lendário atacante francês que jogou por estas equipes fantásticas:",
            options: ["Karim Benzema", "Thierry Henry", "Zinedine Zidane", "Nicolas Anelka"],
            answer: "Thierry Henry",
            crests: [
                { name: "Monaco", color: "bg-red-600 text-white border-zinc-200", abbreviation: "ASM" },
                { name: "Juventus", color: "bg-neutral-900 text-zinc-105 border-zinc-700", abbreviation: "JUV" },
                { name: "Arsenal", color: "bg-red-550 text-white border-zinc-100", abbreviation: "ARS" },
                { name: "FC Barcelona", color: "bg-rose-900 text-blue-200 border-blue-900", abbreviation: "BAR" }
            ]
        },
        {
            id: "q20",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Adivinhe este lendário goleiro italiano que venceu a Copa de 2006 e superou 1000 partidas na carreira:",
            options: ["Gianluigi Buffon", "Iker Casillas", "Manuel Neuer", "Dino Zoff"],
            answer: "Gianluigi Buffon",
            avatarEmoji: "🇮🇹 🧤 👑 Lenda da Juventus | Campeão do Mundo 2006"
        }
    ],
    fr: [
        {
            id: "q1",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Quel pays a remporté la toute première Coupe du Monde de la FIFA en 1930 ?",
            options: ["Argentine", "Uruguay", "Brésil", "Italie"],
            answer: "Uruguay"
        },
        {
            id: "q2",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Qui est le meilleur buteur de l'histoire de la Coupe du Monde de la FIFA avec 16 buts ?",
            options: ["Miroslav Klose", "Ronaldo Nazário", "Gerd Müller", "Pelé"],
            answer: "Miroslav Klose"
        },
        {
            id: "q3",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Un pays d'Afrique a-t-il déjà atteint la finale de la Coupe du Monde de la FIFA ?",
            options: ["Oui", "Non"],
            answer: "Non"
        },
        {
            id: "q4",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "L'icône du football moderne Cristiano Ronaldo a-t-il commencé sa carrière professionnelle au Sporting CP ?",
            options: ["Oui", "Non"],
            answer: "Oui"
        },
        {
            id: "q5",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Devinez la légende grâce à son numéro de maillot emblématique, son pays et son club actuel :",
            options: ["Lionel Messi", "Cristiano Ronaldo", "Diego Maradona", "Zinedine Zidane"],
            answer: "Lionel Messi",
            avatarEmoji: "🐐 🔟 🇦🇷 Inter Miami | Né à Rosario"
        },
        {
            id: "q6",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Devinez l'attaquant français d'après ses exploits et son club actuel :",
            options: ["Kylian Mbappé", "Karim Benzema", "Olivier Giroud", "Antoine Griezmann"],
            answer: "Kylian Mbappé",
            avatarEmoji: "🐢 ⚡ 🇫🇷 Real Madrid | Soulier d'or de la Coupe du Monde"
        },
        {
            id: "q7",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Devinez l'attaquant brésilien ayant remporté deux mondiaux, surnommé El Fenómeno :",
            options: ["Ronaldinho", "Ronaldo Nazário", "Neymar Jr", "Kaká"],
            answer: "Ronaldo Nazário",
            avatarEmoji: "🇧🇷 9️⃣ ⚽ R9 | Vainqueur de la Coupe du Monde 1994 & 2002"
        },
        {
            id: "q8",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifiez le joueur professionnel qui a représenté tous ces grands clubs :",
            options: ["Lionel Messi", "Neymar Jr", "Luis Suárez", "Ronaldinho"],
            answer: "Neymar Jr",
            crests: [
                { name: "Santos FC", color: "bg-zinc-800 text-zinc-100 border-zinc-700", abbreviation: "SAN" },
                { name: "FC Barcelona", color: "bg-red-700 text-blue-300 border-blue-900", abbreviation: "BAR" },
                { name: "Paris Saint-Germain", color: "bg-blue-950 text-red-500 border-red-900", abbreviation: "PSG" },
                { name: "Al Hilal SFC", color: "bg-blue-600 text-white border-blue-400", abbreviation: "HIL" }
            ]
        },
        {
            id: "q9",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "hard",
            question: "Quel attaquant légendaire a joué pour tous ces géants d'Europe ?",
            options: ["Zlatan Ibrahimović", "Cristiano Ronaldo", "Robert Lewandowski", "Karim Benzema"],
            answer: "Zlatan Ibrahimović",
            crests: [
                { name: "Ajax", color: "bg-red-600 text-white border-rose-300", abbreviation: "AJX" },
                { name: "Juventus", color: "bg-neutral-900 text-white border-zinc-700", abbreviation: "JUV" },
                { name: "Inter Milan", color: "bg-slate-900 text-blue-500 border-blue-700", abbreviation: "INT" },
                { name: "Barcelona", color: "bg-emerald-950 text-rose-500 border-rose-800", abbreviation: "BAR" }
            ]
        },
        {
            id: "q10",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Quel joueur détient le record du plus grand nombre de buts sur une seule année (91 buts en 2012) ?",
            options: ["Cristiano Ronaldo", "Robert Lewandowski", "Lionel Messi", "Gerd Müller"],
            answer: "Lionel Messi"
        },
        {
            id: "q11",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "L'Angleterre a-t-elle remporté son unique Coupe du Monde de la FIFA à domicile en 1966 ?",
            options: ["Oui", "Non"],
            answer: "Oui"
        },
        {
            id: "q12",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifiez le milieu de terrain d'élite ayant joué pour ces trois clubs de classe mondiale :",
            options: ["Luka Modrić", "Toni Kroos", "Kevin De Bruyne", "Casemiro"],
            answer: "Toni Kroos",
            crests: [
                { name: "Bayern Munich", color: "bg-red-600 text-white border-red-400", abbreviation: "FCB" },
                { name: "Bayer Leverkusen", color: "bg-red-750 text-black border-zinc-800", abbreviation: "B04" },
                { name: "Real Madrid", color: "bg-white text-zinc-900 border-yellow-500", abbreviation: "RMA" }
            ]
        },
        {
            id: "q13",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Quel pays a remporté le plus grand nombre de titres de Coupe du Monde de la FIFA (5 titres) ?",
            options: ["Allemagne", "Italie", "Argentine", "Brésil"],
            answer: "Brésil"
        },
        {
            id: "q14",
            type: 'yesno',
            theme: "Sport",
            difficulty: "hard",
            question: "L'attaquant uruguayen Luis Suárez est-il le meilleur buteur de l'histoire de la Copa América ?",
            options: ["Oui", "Non"],
            answer: "Non"
        },
        {
            id: "q15",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Devinez l'incroyable milieu de terrain de Premier League d'après son club et son pays :",
            options: ["Kevin De Bruyne", "Luka Modrić", "Andrés Iniesta", "Eden Hazard"],
            answer: "Kevin De Bruyne",
            avatarEmoji: "👑 🪄 🇧🇪 Manchester City | ROI suprême des Passes Décisives 🅰️"
        },
        {
            id: "q16",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifiez l'attaquant prolifique qui a joué pour ces trois célèbres clubs d'Europe :",
            options: ["Erling Haaland", "Jude Bellingham", "Robert Lewandowski", "Harry Kane"],
            answer: "Erling Haaland",
            crests: [
                { name: "Salzburg", color: "bg-red-600 text-white border-zinc-200", abbreviation: "RBS" },
                { name: "Dortmund", color: "bg-yellow-400 text-black border-yellow-600", abbreviation: "BVB" },
                { name: "Manchester City", color: "bg-sky-400 text-slate-800 border-sky-200", abbreviation: "MCI" }
            ]
        },
        {
            id: "q17",
            type: 'abc',
            theme: "Sport",
            difficulty: "hard",
            question: "Quel joueur a remporté le titre de Meilleur Joueur (Ballon d'or) de la Coupe du Monde de la FIFA 2018 ?",
            options: ["Antoine Griezmann", "Luka Modrić", "Eden Hazard", "Kylian Mbappé"],
            answer: "Luka Modrić"
        },
        {
            id: "q18",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Pelé a-t-il remporté trois Coupes du Monde de la FIFA avec le Brésil (1958, 1962 et 1970) ?",
            options: ["Oui", "Non"],
            answer: "Oui"
        },
        {
            id: "q19",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifiez le légendaire attaquant français qui a joué pour toutes ces équipes :",
            options: ["Karim Benzema", "Thierry Henry", "Zinedine Zidane", "Nicolas Anelka"],
            answer: "Thierry Henry",
            crests: [
                { name: "Monaco", color: "bg-red-600 text-white border-zinc-200", abbreviation: "ASM" },
                { name: "Juventus", color: "bg-neutral-900 text-zinc-105 border-zinc-700", abbreviation: "JUV" },
                { name: "Arsenal", color: "bg-red-550 text-white border-zinc-100", abbreviation: "ARS" },
                { name: "FC Barcelona", color: "bg-rose-900 text-blue-200 border-blue-900", abbreviation: "BAR" }
            ]
        },
        {
            id: "q20",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Devinez ce gardien de but légendaire qui a remporté la Coupe du Monde 2006 (Italie) :",
            options: ["Gianluigi Buffon", "Iker Casillas", "Manuel Neuer", "Dino Zoff"],
            answer: "Gianluigi Buffon",
            avatarEmoji: "🇮🇹 🧤 👑 Légende de la Juventus | Champion du Monde 2006"
        }
    ],
    de: [
        {
            id: "q1",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Welches Land gewann 1930 die allererste FIFA-Weltmeisterschaft?",
            options: ["Argentinien", "Uruguay", "Brasilien", "Italien"],
            answer: "Uruguay"
        },
        {
            id: "q2",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Wer ist der erfolgreichste Torschütze der Geschichte der WM mit 16 Treffern?",
            options: ["Miroslav Klose", "Ronaldo Nazário", "Gerd Müller", "Pelé"],
            answer: "Miroslav Klose"
        },
        {
            id: "q3",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Hat jemals ein Land aus Afrika das Finale der FIFA-Weltmeisterschaft erreicht?",
            options: ["Ja", "Nein"],
            answer: "Nein"
        },
        {
            id: "q4",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Hat die Ikone Cristiano Ronaldo seine Profikarriere bei Sporting CP gestartet?",
            options: ["Ja", "Nein"],
            answer: "Ja"
        },
        {
            id: "q5",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Errate die Legende anhand von Rückennummer, Heimatland und aktuellem Club:",
            options: ["Lionel Messi", "Cristiano Ronaldo", "Diego Maradona", "Zinedine Zidane"],
            answer: "Lionel Messi",
            avatarEmoji: "🐐 🔟 🇦🇷 Inter Miami | Geboren in Rosario"
        },
        {
            id: "q6",
            type: 'player_image',
            theme: "Sport",
            difficulty: "easy",
            question: "Errate den französischen Stürmer anhand seiner Erfolge und seines aktuellen Vereins:",
            options: ["Kylian Mbappé", "Karim Benzema", "Olivier Giroud", "Antoine Griezmann"],
            answer: "Kylian Mbappé",
            avatarEmoji: "🐢 ⚡ 🇫🇷 Real Madrid | Goldener Schuh bei der WM"
        },
        {
            id: "q7",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Errate den brasilianischen Stürmer, der zwei Weltmeisterschaften gewann und El Fenómeno genannt wird:",
            options: ["Ronaldinho", "Ronaldo Nazário", "Neymar Jr", "Kaká"],
            answer: "Ronaldo Nazário",
            avatarEmoji: "🇧🇷 9️⃣ ⚽ R9 | Weltmeister 1994 & 2002"
        },
        {
            id: "q8",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Erkenne den Spieler, der im Laufe seiner Karriere für all diese Vereine gespielt hat:",
            options: ["Lionel Messi", "Neymar Jr", "Luis Suárez", "Ronaldinho"],
            answer: "Neymar Jr",
            crests: [
                { name: "Santos FC", color: "bg-zinc-800 text-zinc-100 border-zinc-700", abbreviation: "SAN" },
                { name: "FC Barcelona", color: "bg-red-700 text-blue-300 border-blue-900", abbreviation: "BAR" },
                { name: "Paris Saint-Germain", color: "bg-blue-950 text-red-500 border-red-900", abbreviation: "PSG" },
                { name: "Al Hilal SFC", color: "bg-blue-600 text-white border-blue-400", abbreviation: "HIL" }
            ]
        },
        {
            id: "q9",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "hard",
            question: "Welcher legendäre Stürmer hat für all diese europäischen Giganten gespielt?",
            options: ["Zlatan Ibrahimović", "Cristiano Ronaldo", "Robert Lewandowski", "Karim Benzema"],
            answer: "Zlatan Ibrahimović",
            crests: [
                { name: "Ajax", color: "bg-red-600 text-white border-rose-300", abbreviation: "AJX" },
                { name: "Juventus", color: "bg-neutral-900 text-white border-zinc-700", abbreviation: "JUV" },
                { name: "Inter Milan", color: "bg-slate-900 text-blue-500 border-blue-700", abbreviation: "INT" },
                { name: "Barcelona", color: "bg-emerald-950 text-rose-500 border-rose-800", abbreviation: "BAR" }
            ]
        },
        {
            id: "q10",
            type: 'abc',
            theme: "Sport",
            difficulty: "medium",
            question: "Welcher Spieler hält den Rekord für die meisten Kalenderjahr-Tore (91 Tore in 2012)?",
            options: ["Cristiano Ronaldo", "Robert Lewandowski", "Lionel Messi", "Gerd Müller"],
            answer: "Lionel Messi"
        },
        {
            id: "q11",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Hat England 1966 seine einzige Weltmeisterschaft im eigenen Land geholt?",
            options: ["Ja", "Nein"],
            answer: "Ja"
        },
        {
            id: "q12",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Erkenne den Weltklasse-Spielmacher im Mittelfeld, der für diese Spitzenvereine gespielt hat:",
            options: ["Luka Modrić", "Toni Kroos", "Kevin De Bruyne", "Casemiro"],
            answer: "Toni Kroos",
            crests: [
                { name: "Bayern Munich", color: "bg-red-600 text-white border-red-400", abbreviation: "FCB" },
                { name: "Bayer Leverkusen", color: "bg-red-750 text-black border-zinc-800", abbreviation: "B04" },
                { name: "Real Madrid", color: "bg-white text-zinc-900 border-yellow-500", abbreviation: "RMA" }
            ]
        },
        {
            id: "q13",
            type: 'abc',
            theme: "Sport",
            difficulty: "easy",
            question: "Welches Land hat die meisten FIFA-Weltmeistertitel (5 Titel) gewonnen?",
            options: ["Deutschland", "Italien", "Argentinien", "Brasilien"],
            answer: "Brasilien"
        },
        {
            id: "q14",
            type: 'yesno',
            theme: "Sport",
            difficulty: "hard",
            question: "Ist der uruguayische Stürmer Luis Suárez der aktuelle Allzeit-Toptorschütze der Copa América?",
            options: ["Ja", "Nein"],
            answer: "Nein"
        },
        {
            id: "q15",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Errate den überragenden Premier-League-Spieler anhand seines Vereins und seines Lands:",
            options: ["Kevin De Bruyne", "Luka Modrić", "Andrés Iniesta", "Eden Hazard"],
            answer: "Kevin De Bruyne",
            avatarEmoji: "👑 🪄 🇧🇪 Manchester City | Ultimativer Assist-König 🅰️"
        },
        {
            id: "q16",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Identifiziere den treffsicheren Stürmer, der für diese drei Klubs gespielt hat:",
            options: ["Erling Haaland", "Jude Bellingham", "Robert Lewandowski", "Harry Kane"],
            answer: "Erling Haaland",
            crests: [
                { name: "Salzburg", color: "bg-red-600 text-white border-zinc-200", abbreviation: "RBS" },
                { name: "Dortmund", color: "bg-yellow-400 text-black border-yellow-600", abbreviation: "BVB" },
                { name: "Manchester City", color: "bg-sky-400 text-slate-800 border-sky-200", abbreviation: "MCI" }
            ]
        },
        {
            id: "q17",
            type: 'abc',
            theme: "Sport",
            difficulty: "hard",
            question: "Welcher Spieler gewann den Goldenen Ball der Weltmeisterschaft 2018 in Russland?",
            options: ["Antoine Griezmann", "Luka Modrić", "Eden Hazard", "Kylian Mbappé"],
            answer: "Luka Modrić"
        },
        {
            id: "q18",
            type: 'yesno',
            theme: "Sport",
            difficulty: "easy",
            question: "Hat Pelé drei Weltmeisterschaften mit Brasilien gewonnen (1958, 1962 und 1970)?",
            options: ["Ja", "Nein"],
            answer: "Ja"
        },
        {
            id: "q19",
            type: 'club_crest',
            theme: "Sport",
            difficulty: "medium",
            question: "Erkenne den legendären französischen Stürmer, der für diese Klubs aktiv war:",
            options: ["Karim Benzema", "Thierry Henry", "Zinedine Zidane", "Nicolas Anelka"],
            answer: "Thierry Henry",
            crests: [
                { name: "Monaco", color: "bg-red-600 text-white border-zinc-200", abbreviation: "ASM" },
                { name: "Juventus", color: "bg-neutral-900 text-zinc-105 border-zinc-700", abbreviation: "JUV" },
                { name: "Arsenal", color: "bg-red-550 text-white border-zinc-100", abbreviation: "ARS" },
                { name: "FC Barcelona", color: "bg-rose-900 text-blue-200 border-blue-900", abbreviation: "BAR" }
            ]
        },
        {
            id: "q20",
            type: 'player_image',
            theme: "Sport",
            difficulty: "medium",
            question: "Errate diesen legendären italienischen Torwart, der die Weltmeisterschaft 2006 gewann:",
            options: ["Gianluigi Buffon", "Iker Casillas", "Manuel Neuer", "Dino Zoff"],
            answer: "Gianluigi Buffon",
            avatarEmoji: "🇮🇹 🧤 👑 Juventus-Legende | Weltmeister 2006"
        }
    ]
};

// Backwards-compatible fallback list
export const ALL_QUESTIONS: QuizQuestion[] = LOCALIZED_QUESTIONS['en'];

// Helper to deterministically shuffle and pick 10 questions of the day (supporting language matching)
export const getDailyQuestions = (dateString: string, lang: string = 'en'): QuizQuestion[] => {
    const currentLang = LOCALIZED_QUESTIONS[lang] ? lang : 'en';
    const questionsList = LOCALIZED_QUESTIONS[currentLang] || LOCALIZED_QUESTIONS['en'];

    // Generate a simple hash based on the date string (e.g. "2026-06-05")
    let hash = 0;
    for (let i = 0; i < dateString.length; i++) {
        hash = dateString.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);

    const shuffled = [...questionsList];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = (hash + i) % (i + 1);
        const temp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = temp;
    }
    return shuffled.slice(0, 10);
};

// Helper to get local date string yyyy-mm-dd (avoiding UTC timezone shift bugs)
export const getLocalDateString = (date = new Date()): string => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().split('T')[0];
};
